import { FC, MouseEvent, useState } from 'react';
import { Popover, Slider, Tooltip } from '@mui/material';
import styled from 'styled-components';
import { editorTheme } from '../../theme';
import NumberField from '../Properties/NumberField';
import { ToolButton } from './ToolbarControls';
import { sharedValue, useSelectedText } from './useSelectedText';

// In Fabric's units: line spacing is a multiple of the font size, letter
// spacing is in thousandths of an em.
const SETTINGS = [
  {
    key: 'lineHeight',
    label: 'Line spacing',
    min: 0.5,
    max: 3,
    step: 0.05,
    decimals: 2,
    fallback: 1.16,
  },
  {
    key: 'charSpacing',
    label: 'Letter spacing',
    min: -200,
    max: 800,
    step: 10,
    decimals: 0,
    fallback: 0,
  },
] as const;

type SpacingKey = typeof SETTINGS[number]['key'];

const Panel = styled.div`
  width: 260px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.span`
  font-size: 12px;
  color: ${editorTheme.textMuted};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  & > :last-child {
    width: 64px;
    flex: none;
  }
`;

const SpacingIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M10 6h10M10 12h10M10 18h10M4 4v16M2 6l2-2 2 2M2 18l2 2 2-2" />
  </svg>
);

const TextSpacing: FC = () => {
  const { objects, apply, preview } = useSelectedText();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // The value under a slider that is being dragged.
  const [dragging, setDragging] = useState<Partial<Record<SpacingKey, number>>>(
    {},
  );

  return (
    <>
      <Tooltip title="Line and letter spacing">
        <ToolButton
          aria-label="Line and letter spacing"
          aria-haspopup="dialog"
          aria-expanded={!!anchor}
          active={!!anchor}
          onClick={(event: MouseEvent<HTMLButtonElement>) =>
            setAnchor(event.currentTarget)
          }
        >
          <SpacingIcon />
        </ToolButton>
      </Tooltip>
      <Popover
        open={!!anchor && !!objects.length}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ role: 'dialog', 'aria-label': 'Line and letter spacing' }}
      >
        <Panel>
          {SETTINGS.map((setting) => {
            const shared = sharedValue(
              objects,
              (object) => object[setting.key] ?? setting.fallback,
            );
            const sliderValue =
              dragging[setting.key] ??
              shared ??
              objects[0]?.[setting.key] ??
              setting.fallback;
            return (
              <Row key={setting.key}>
                <Label id={`text-${setting.key}`}>{setting.label}</Label>
                <Controls>
                  <Slider
                    size="small"
                    aria-labelledby={`text-${setting.key}`}
                    min={setting.min}
                    max={setting.max}
                    step={setting.step}
                    value={sliderValue}
                    sx={{ color: editorTheme.accent }}
                    onChange={(_, value) => {
                      setDragging({ [setting.key]: value as number });
                      preview({ [setting.key]: value as number });
                    }}
                    onChangeCommitted={(_, value) => {
                      setDragging({});
                      apply({ [setting.key]: value as number });
                    }}
                  />
                  <NumberField
                    label={setting.label}
                    prefix=""
                    value={dragging[setting.key] ?? shared}
                    decimals={setting.decimals}
                    min={setting.min}
                    max={setting.max}
                    onCommit={(value) => apply({ [setting.key]: value })}
                  />
                </Controls>
              </Row>
            );
          })}
        </Panel>
      </Popover>
    </>
  );
};

export default TextSpacing;
